import React, { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { CalendarClock, Check } from "lucide-react";
import { format } from "date-fns";

const slots = ["09:00","10:00","11:00","13:00","14:00","15:30","17:00"];

export default function Booking() {
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ name: "", email: "", notes: "" });
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) return toast.error("Pick a date and time.");
    setBusy(true);
    try {
      const iso = format(date, "yyyy-MM-dd");
      const { data } = await api.post("/bookings", { ...form, date: iso, time, timezone: tz });
      setDone(data.booking_id);
      toast.success("Call booked. Confirmation email on the way.");
    } catch (err) {
      toast.error(errMsg(err, "Booking failed"));
    } finally {
      setBusy(false);
    }
  };

  if (done) return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="booking-success">
      <Check size={40} className="mx-auto text-emerald-400"/>
      <h1 className="font-display font-black text-4xl mt-4">You're on the calendar.</h1>
      <p className="text-white/60 mt-3">Confirmation <span className="font-mono">#{done}</span> sent to {form.email}.</p>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-6 py-16" data-testid="booking-page">
      <div className="label-mono mb-3">Discovery call · 30 minutes</div>
      <h1 className="font-display font-semibold text-4xl sm:text-6xl max-w-3xl">A useful first conversation, not a sales script.</h1>
      <p className="text-white/60 mt-5 max-w-2xl leading-relaxed">Tell us what you are trying to change. We’ll discuss the opportunity, likely constraints and the most sensible next step. There is no obligation.</p>

      <form onSubmit={submit} className="mt-12 grid md:grid-cols-2 gap-10">
        <div className="glass rounded-2xl p-6">
          <div className="label-mono mb-3">Pick a date</div>
          <Calendar mode="single" selected={date} onSelect={setDate} disabled={{ before: new Date() }}
            className="bg-transparent text-white [--rdp-cell-size:2.4rem]" data-testid="booking-calendar"/>
          <div className="label-mono mt-6 mb-3">Time slots · {tz}</div>
          <div className="flex flex-wrap gap-2">
            {slots.map(s => (
              <button type="button" key={s} onClick={()=>setTime(s)}
                data-testid={`slot-${s}`}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${time===s ? "bg-violet-500 border-violet-400 text-white" : "border-white/15 text-white/70 hover:border-white/40"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="glass rounded-2xl p-6 flex flex-col">
          <div className="label-mono mb-4">Your details</div>
          <label htmlFor="booking-name" className="text-sm font-medium mb-2">Full name</label>
          <input id="booking-name" name="name" autoComplete="name" placeholder="Your name" required value={form.name} onChange={e=>setForm({...form,name:e.target.value})}
            data-testid="booking-name" className="bg-transparent hairline-strong rounded-lg px-4 py-3 mb-3 outline-none focus:border-violet-500"/>
          <label htmlFor="booking-email" className="text-sm font-medium mb-2 mt-1">Work email</label>
          <input id="booking-email" name="email" autoComplete="email" placeholder="you@company.com" required type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}
            data-testid="booking-email" className="bg-transparent hairline-strong rounded-lg px-4 py-3 mb-3 outline-none focus:border-violet-500"/>
          <label htmlFor="booking-notes" className="text-sm font-medium mb-2 mt-1">What would you like to improve?</label>
          <textarea id="booking-notes" name="notes" placeholder="A short note about the product, audience and desired outcome" rows={5} maxLength={1200} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})}
            data-testid="booking-notes" className="bg-transparent hairline-strong rounded-lg px-4 py-3 outline-none focus:border-violet-500"/>
          <div className="mt-auto pt-6 flex items-center justify-between">
            <div className="text-xs text-white/50 flex items-center gap-2"><CalendarClock size={14}/>{date ? `${date.toDateString()}${time?` @ ${time}`:""}` : "Select date & time"}</div>
            <button type="submit" disabled={busy} className="pill-btn accent disabled:opacity-50 disabled:cursor-not-allowed" data-testid="booking-submit">{busy ? "Booking…" : "Confirm booking"}</button>
          </div>
        </div>
      </form>
    </div>
  );
}
